import type { ConfidenceResult, ScoredSituation, SituationDimensions } from './types';
import { clamp01, mean } from './utils';

type ConfidenceInput = {
  sortedScores: ScoredSituation[];
  dimensions: SituationDimensions;
  temporalStability: number;
  persistence: number;
  transitionLikelihood: number;
};

function featureAgreement(topKey: ScoredSituation['key'], dimensions: SituationDimensions): number {
  if (topKey === 'single_speaker') {
    return mean([dimensions.dominance, dimensions.speechCoherence, 1 - dimensions.noiseIntensity]);
  }
  if (topKey === 'conversation') {
    return mean([dimensions.turnTaking, dimensions.speechCoherence, 1 - Math.abs(dimensions.dominance - 0.45)]);
  }
  if (topKey === 'babble') {
    return mean([dimensions.diffuseness, dimensions.noiseIntensity, 1 - dimensions.speechCoherence]);
  }
  if (topKey === 'shouting') {
    return mean([dimensions.noiseIntensity, dimensions.dominance, 1 - dimensions.speechCoherence]);
  }
  return mean([dimensions.noiseIntensity, 1 - dimensions.speechCoherence, dimensions.rhythmicity]);
}

export function computeConfidence(input: ConfidenceInput): ConfidenceResult {
  const top = input.sortedScores[0];
  const second = input.sortedScores[1];
  if (!top) {
    return {
      confidence: 0,
      transitionLikelihood: 1,
      competingSituations: []
    };
  }

  const ambiguity = second ? clamp01(1 - (top.score - second.score)) : 0.2;
  const agreement = featureAgreement(top.key, input.dimensions);
  const confidence = clamp01(
    top.score * 0.35 +
      input.temporalStability * 0.2 +
      agreement * 0.25 +
      input.persistence * 0.15 +
      (1 - ambiguity) * 0.05
  );

  return {
    confidence: Number(confidence.toFixed(3)),
    transitionLikelihood: Number(clamp01((input.transitionLikelihood + ambiguity) / 2).toFixed(3)),
    competingSituations: input.sortedScores
      .slice(0, 3)
      .filter((entry) => entry.score >= 0.2)
      .map((entry) => ({ key: entry.key, score: Number(entry.score.toFixed(3)) }))
  };
}
